import * as React from 'react';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Stack, Avatar, ButtonBase, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useAuthContext } from 'src/auth/hooks';
import { useEntityContext } from 'src/entity/hooks';
import { useGetMySubentities } from 'src/api/entity';
import { useGetUserPermissions } from 'src/api/users-entities';

import Iconify from 'src/components/iconify';

export default function MySubentitiesMenu({ children, itemHref, sx, ...other }) {
  const { user } = useAuthContext();
  const { entity: currentEntity, entity_id } = useEntityContext();
  const { entities: myEntities } = useGetMySubentities(currentEntity.parent || entity_id);
  const { permissions: userPermissions } = useGetUserPermissions(user?.id);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  // Filtrer les entités selon les permissions
  const filteredEntities = React.useMemo(() => {
    // Super admin voit tout
    if (user?.role === 'superadmin') return myEntities;
    // Vérifier si l'utilisateur est admin de l'entité parente
    const parentEntityId = currentEntity.parent ? currentEntity.parent : currentEntity.id;
    const parentPermission = userPermissions?.find((p) => p.entity_id === parentEntityId);
    const isParentAdmin = parentPermission?.is_admin || false;

    // Admin de l'entité parente voit toutes les filles
    if (isParentAdmin) return myEntities;

    // Autres utilisateurs : filtrer selon leurs permissions
    return myEntities.filter((entity) => {
      const entityPermission = userPermissions?.find((p) => p.entity_id === entity.id);
      return entityPermission?.can_access_ges || entityPermission?.is_admin;
    });
  }, [user?.role, myEntities, currentEntity, userPermissions]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const RenderCheckmark = useCallback(
    ({ entityId }) => {
      if (!currentEntity) return null;
      if (entityId === currentEntity.id) return <Iconify icon="material-symbols:check" />;
      return null;
    },
    [currentEntity]
  );

  return (
    <>
      <ButtonBase
        sx={sx}
        id="myentities-button"
        aria-controls={open ? 'myentities-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        {...other}
      >
        {children}
      </ButtonBase>
      <Menu
        id="myentities-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'myentities-button',
        }}
      >
        {filteredEntities.map((entity) => {
          const { id, name = '', parent, logo = 'test' } = entity;

          return (
            <MenuItem
              component="a"
              href={
                itemHref
                  ? itemHref(id, parent)
                  : paths.entity.modules.ges.dashboard.root(id, parent)
              }
              key={id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack gap={1} alignItems="center" direction="row">
                <Avatar
                  alt={name}
                  src={logo}
                  sx={{ width: 24, height: 24 }}
                  variant="square"
                  imgProps={{ style: { objectFit: 'contain' } }}
                >
                  {name[0] || '?'}
                </Avatar>
                <Typography variant="body2">{name}</Typography>
                <RenderCheckmark entityId={id} />
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

MySubentitiesMenu.propTypes = {
  sx: PropTypes.object,
  children: PropTypes.node,
  itemHref: PropTypes.func,
};
